import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileTextIcon, CheckIcon, AlertTriangleIcon, GiftIcon } from 'lucide-react';
import { ContactSection } from '../components/ContactSection';
import { useNavigate } from 'react-router-dom';
import { trackLead } from '../utils/analytics';
export const FreeTipsPage: React.FC = () => {
  const navigate = useNavigate();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const handleLeadClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    // Track Lead event before redirect
    trackLead('Free Tips PDF', 'free_tips');
    setIsRedirecting(true);
    // Redirect after 1.5 seconds
    setTimeout(() => {
      window.location.href = 'https://buy.stripe.com/fZu3cucIH7LY6wt2vAg7e03';
    }, 1500);
  };
  return <div className="bg-black text-white min-h-screen" data-id="element-1620">
      {/* Simple Header with Logo */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-md border-b border-gray-800/50" data-id="element-1621">
        <div className="container mx-auto px-4 py-4" data-id="element-1622">
          <button onClick={() => navigate('/')} className="text-2xl md:text-3xl font-black tracking-tight text-glow hover:text-green-400 transition-colors duration-300" data-id="element-1623">
            BODY-HACK.CZ
          </button>
        </div>
      </header>

      {/* Main Content - Centered Box */}
      <main className="pt-24 pb-12 min-h-screen flex items-center justify-center px-4" data-id="element-1624">
        <div className="container mx-auto max-w-4xl" data-id="element-1625">
          {/* Ambient background glow */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-3xl h-80 bg-green-500/10 blur-[100px] rounded-full pointer-events-none" data-id="element-1626" />

          <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} className="relative" data-id="element-1627">
            {/* Gradient Border Wrapper */}
            <div className="absolute -inset-[2px] bg-gradient-to-br from-green-300/50 via-green-600/30 to-green-900/50 rounded-2xl blur-sm" data-id="element-1628" />

            <div className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl p-6 md:p-12 flex flex-col lg:flex-row items-center gap-6 lg:gap-12 shadow-2xl shadow-green-900/20 border border-white/10" data-id="element-1629">
              {/* Left side - Premium Icon */}
              <div className="flex-shrink-0 relative group" data-id="element-1630">
                <div className="absolute inset-0 bg-green-500/30 blur-xl rounded-full group-hover:bg-green-500/40 transition-all duration-500" data-id="element-1631" />
                <div className="relative w-28 h-28 md:w-32 md:h-32 rounded-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900 border-2 border-green-500/40 shadow-inner shadow-green-500/20" data-id="element-1632">
                  <FileTextIcon className="w-12 h-12 md:w-14 md:h-14 text-green-400 drop-shadow-[0_0_15px_rgba(74,222,128,0.4)]" strokeWidth={1.5} data-id="element-1633" />
                </div>
                <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-green-600 to-green-500 text-black text-[10px] tracking-widest font-black px-4 py-1.5 rounded-full shadow-xl shadow-green-900/60 uppercase border-2 border-green-400/60" data-id="element-1634">
                  Zdarma
                </div>
              </div>

              {/* Right side - Content */}
              <div className="flex-1 text-center lg:text-left" data-id="element-1635">
                <motion.h1 initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.2,
                duration: 0.6
              }} className="text-2xl sm:text-3xl md:text-4xl font-black mb-4 tracking-tight leading-tight" data-id="element-1636">
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-green-200 via-green-400 to-green-600 drop-shadow-lg" data-id="element-1637">
                    ZÍSKEJ ZDARMA
                  </span>
                  <br data-id="element-1638" />
                  <span className="text-white mt-1 block" data-id="element-1639">
                    TIPY PRO RYCHLEJŠÍ PROGRESS
                  </span>
                </motion.h1>

                <motion.p initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.3,
                duration: 0.6
              }} className="text-slate-300 mb-6 leading-relaxed font-light text-sm sm:text-base md:text-lg" data-id="element-1640">
                  Chceš se dozvědět tajemství, která mohou dramaticky zrychlit
                  tvůj progress? Připravil jsem PDF plné tipů a rad, které ti
                  pomohou posunout tvůj trénink a výsledky na další úroveň.
                </motion.p>

                <motion.div initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.4,
                duration: 0.6
              }} className="space-y-3 mb-8" data-id="element-1641">
                  <div className="flex items-start gap-3 text-slate-200 group" data-id="element-1642">
                    <div className="p-1.5 rounded-full bg-green-500/15 border border-green-500/30 mt-0.5 group-hover:border-green-500/50 group-hover:bg-green-500/20 transition-all duration-300 flex-shrink-0" data-id="element-1643">
                      <CheckIcon className="w-4 h-4 md:w-5 md:h-5 text-green-400" data-id="element-1644" />
                    </div>
                    <span className="font-medium text-sm sm:text-base" data-id="element-1645">
                      Tajné tipy pro maximální efektivitu tréninku
                    </span>
                  </div>
                  <div className="flex items-start gap-3 text-slate-200 group" data-id="element-1646">
                    <div className="p-1.5 rounded-full bg-green-500/15 border border-green-500/30 mt-0.5 group-hover:border-green-500/50 group-hover:bg-green-500/20 transition-all duration-300 flex-shrink-0" data-id="element-1647">
                      <AlertTriangleIcon className="w-4 h-4 md:w-5 md:h-5 text-green-500" data-id="element-1648" />
                    </div>
                    <span className="font-medium text-sm sm:text-base" data-id="element-1649">
                      Nejčastější chyby, které brzdí tvůj progress
                    </span>
                  </div>
                  <div className="flex items-start gap-3 text-slate-200 group" data-id="element-1650">
                    <div className="p-1.5 rounded-full bg-green-500/15 border border-green-500/30 mt-0.5 group-hover:border-green-500/50 group-hover:bg-green-500/20 transition-all duration-300 flex-shrink-0" data-id="element-1651">
                      <GiftIcon className="w-4 h-4 md:w-5 md:h-5 text-green-400" data-id="element-1652" />
                    </div>
                    <span className="font-medium text-sm sm:text-base" data-id="element-1653">
                      2 bonusové rady které mně pomohly s rapidním růstem
                    </span>
                  </div>
                </motion.div>

                <motion.div initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.5,
                duration: 0.6
              }} className="inline-block w-full lg:w-auto" data-id="element-1654">
                  <motion.button onClick={handleLeadClick} disabled={isRedirecting} whileHover={{
                  scale: isRedirecting ? 1 : 1.03,
                  boxShadow: isRedirecting ? undefined : '0 25px 50px -10px rgba(34, 197, 94, 0.4)'
                }} whileTap={{
                  scale: isRedirecting ? 1 : 0.98
                }} className="w-full lg:w-auto relative group overflow-hidden bg-gradient-to-r from-green-600 via-green-500 to-green-600 bg-[length:200%_auto] hover:bg-[position:right_center] text-black font-black py-4 px-10 md:px-12 rounded-xl transition-all duration-500 shadow-2xl shadow-green-900/30 border-2 border-green-400/60 text-base md:text-lg disabled:opacity-100 disabled:cursor-default" data-id="element-1655">
                    <span className="relative z-10 tracking-wider" data-id="element-1656">
                      {isRedirecting ? 'PŘESMĚROVÁVÁM...' : 'MÁM ZÁJEM!'}
                    </span>
                    <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-500 skew-y-12" data-id="element-1657" />
                  </motion.button>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </main>

      {/* Contact Section */}
      <ContactSection data-id="element-1658" />

      {/* Redirecting Overlay */}
      <AnimatePresence data-id="element-1659">
        {isRedirecting && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/95 backdrop-blur-sm z-50 flex items-center justify-center" data-id="element-1660">
            <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          delay: 0.1
        }} className="text-center" data-id="element-1661">
              <div className="mb-6" data-id="element-1662">
                <motion.div animate={{
              rotate: 360
            }} transition={{
              duration: 1,
              repeat: Infinity,
              ease: 'linear'
            }} className="w-16 h-16 border-4 border-green-500 border-t-transparent rounded-full mx-auto" data-id="element-1663" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2" data-id="element-1664">
                Přesměrovávám na tipy zdarma...
              </h3>
              <p className="text-gray-400" data-id="element-1665">Moment prosím</p>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
};